import React, { useState } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Database, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const ratingTableSeeds = [
  {
    benefit_type: 'gap',
    name: '2025 GAP Age-Banded Rates',
    rating_method: 'age_banded',
    rates: {
      39: 10.50,
      49: 15.25,
      59: 22.00,
      69: 35.50
    },
    effective_date: '2025-01-01',
    notes: 'Standard GAP insurance age-banded rates for 2025'
  },
  {
    benefit_type: 'hsp',
    name: '2025 Hospital Indemnity Age/Gender Rates',
    rating_method: 'age_gender',
    rates: {
      M: {
        39: 18.50,
        49: 28.75,
        59: 42.50,
        69: 68.00
      },
      F: {
        39: 16.50,
        49: 25.75,
        59: 38.50,
        69: 62.00
      }
    },
    effective_date: '2025-01-01',
    notes: 'Hospital Indemnity rates by age and gender'
  },
  {
    benefit_type: 'dental',
    name: '2025 Dental Geographic Zones',
    rating_method: 'geographic',
    rates: {
      zone1: 25.00,
      zone2: 30.00,
      zone3: 37.50
    },
    effective_date: '2025-01-01',
    notes: 'Dental rates by geographic zone (Zone 1: Rural, Zone 2: Suburban, Zone 3: Urban)'
  },
  {
    benefit_type: 'life',
    name: '2025 Life Insurance Age/Tobacco Rates (per $1000)',
    rating_method: 'age_banded',
    rates: {
      29: 0.08,
      39: 0.12,
      49: 0.22,
      59: 0.45,
      69: 0.95,
      tobacco_multiplier: 1.5
    },
    effective_date: '2025-01-01',
    notes: 'Life insurance rates per $1,000 of coverage. Tobacco users multiply by 1.5x'
  },
  {
    benefit_type: 'critical',
    name: '2025 Critical Illness Age-Banded Rates',
    rating_method: 'age_banded',
    rates: {
      39: 12.50,
      49: 22.50,
      59: 38.50,
      69: 72.00
    },
    effective_date: '2025-01-01',
    notes: 'Critical illness coverage age-banded rates'
  },
  {
    benefit_type: 'accident',
    name: '2025 Accident Insurance Flat Rate',
    rating_method: 'flat',
    rates: {
      employee_only: 8.50,
      employee_spouse: 14.50,
      employee_children: 12.50,
      family: 18.50
    },
    effective_date: '2025-01-01',
    notes: 'Accident insurance flat rates by coverage tier'
  },
  {
    benefit_type: 'vision',
    name: '2025 Vision Insurance Flat Rate',
    rating_method: 'flat',
    rates: {
      employee_only: 6.50,
      employee_spouse: 11.00,
      employee_children: 9.50,
      family: 14.50
    },
    effective_date: '2025-01-01',
    notes: 'Vision insurance flat rates by coverage tier'
  }
];

export default function RatingTableSeedData({ onComplete }) {
  const [loading, setLoading] = useState(false);
  const [seeded, setSeeded] = useState(false);

  const seedRatingTables = async () => {
    setLoading(true);
    try {
      await api.entities.RatingTable.bulkCreate(ratingTableSeeds);
      setSeeded(true);
      toast.success(`Successfully seeded ${ratingTableSeeds.length} rating tables`);
      if (onComplete) onComplete();
    } catch (err) {
      console.error(err);
      toast.error('Failed to seed rating tables');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          Seed Standard Rating Tables
        </CardTitle>
        <CardDescription>
          Quick-load standard benefit rating structures for 2025
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Load industry-standard rates for GAP, Hospital, Dental, Life, Critical Illness, Accident, and Vision insurance.
        </p>

        {seeded ? (
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="w-5 h-5" />
            <span className="font-medium">Rating tables seeded successfully!</span>
          </div>
        ) : (
          <Button
            onClick={seedRatingTables}
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-800 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Seeding...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Seed 2025 Rating Tables
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}